/*
 * The sidebar, as a component — M-25, change order 06.
 *
 * ── Why it moved here ───────────────────────────────────────────────────────────────────────────
 *
 * Both applications hand-rendered the same eleven lines of markup: a `div.tf-nav`, a `Link` per
 * item, `tf-nav__link` on each. Identical, in two files, with nothing holding them together — so
 * the grouping this slice introduces would have had to be written twice and would have drifted the
 * first time one of them changed. `AppShell.nav` stays an opaque `ReactNode`, which is what lets
 * this land without touching the shell at all.
 *
 * ── Why it takes a render function instead of a link component ──────────────────────────────────
 *
 * This package has no router and must not acquire one. `@tanstack/react-router`'s `Link` is typed
 * against each app's own route tree, so accepting it as a component prop would either import the
 * router here or type the prop as `any` — and the second is the first with the failure moved to
 * runtime. `renderLink` hands the app its own item back plus the class name to put on it, and the
 * app returns whatever its router wants. The design system keeps the structure, the spacing and
 * the two states; the app keeps navigation.
 *
 * ── The headingless group ───────────────────────────────────────────────────────────────────────
 *
 * `eyebrow: null` renders the group with no heading, which is how Home sits above the three
 * sections without being a section of one. It is a property of the group rather than a special
 * case for a known route id, so the admin console — which has three items and no need for
 * headings at all — passes a single headingless group and gets exactly the flat list it had.
 *
 * An empty group renders nothing rather than a heading with nothing under it. That is not
 * hypothetical: every item is filtered by entitlement and permission before it arrives here, so a
 * tenant without the analytics module hands INSIGHTS an empty list.
 */

import type { ReactNode } from 'react';

export interface NavItem {
  /** Stable across renders and unique within the whole nav — a route id, in practice. */
  readonly key: string;
  /** Already translated. This package never resolves a message key. */
  readonly label: string;
  readonly href: string;
  /**
   * The glyph shown beside the label, and *instead of* it when the column is docked.
   *
   * An opaque node for the same reason the link is one: what a screen is called and what it looks
   * like are both the app's to say. Optional, because the admin console has three items and no
   * reason to dock — an item with no glyph simply renders without one.
   */
  readonly icon?: ReactNode;
}

export interface NavGroup {
  readonly key: string;
  /** Already translated, or `null` for a group that renders without a heading. */
  readonly eyebrow: string | null;
  readonly items: readonly NavItem[];
}

export interface NavProps {
  readonly groups: readonly NavGroup[];
  /**
   * The app's own link, given the item and the class name to carry.
   *
   * The class name is passed rather than assumed so that the app cannot accidentally drop it —
   * `tf-nav__link` is what carries the hover and the current-page states, and a link without it
   * renders as unstyled body text that still works, which is the worst kind of regression.
   */
  readonly renderLink: (item: NavItem, className: string, content: ReactNode) => ReactNode;
  /** Names the landmark for a screen reader. Every app has more than one navigation region. */
  readonly label: string;
  /**
   * Docked: glyphs only, labels carried for a screen reader and in a tooltip.
   *
   * **What the caller must not do is render a different nav.** Same items, same order, same
   * grouping — one column in two widths. A separate collapsed list is how the two stop agreeing
   * about what is in the product.
   */
  readonly collapsed?: boolean;
}

export function Nav({ groups, renderLink, label, collapsed = false }: NavProps) {
  const populated = groups.filter((group) => group.items.length > 0);

  /*
   * What goes inside the link.
   *
   * Docked, the label does not disappear — it becomes visually hidden and a `title`. A rail of
   * unnamed pictures is unusable by a screen reader and merely guessable by everybody else, and
   * the two failures have the same fix. The glyph is `aria-hidden` at its source, so the link is
   * announced by exactly one string in both states.
   */
  const content = (item: NavItem): ReactNode =>
    collapsed && item.icon ? (
      <>
        {item.icon}
        <span className="tf-visually-hidden">{item.label}</span>
      </>
    ) : (
      <>
        {item.icon}
        <span className="tf-nav__label">{item.label}</span>
      </>
    );

  return (
    <nav className={collapsed ? 'tf-nav tf-nav--collapsed' : 'tf-nav'} aria-label={label}>
      {populated.map((group) => (
        /*
         * `aria-labelledby` rather than `aria-label`, so the heading a sighted reader sees and the
         * name a screen reader announces are the same string by construction. Two sources for one
         * name is how they end up disagreeing after a translation change.
         */
        <div
          key={group.key}
          className="tf-nav__section"
          role="group"
          aria-labelledby={group.eyebrow === null ? undefined : `tf-nav-${group.key}`}
        >
          {/*
            Docked, the heading is still the group's accessible name — it is hidden from sight, not
            removed. A hairline above the group does the work the word did: it says *these belong
            together* in the two characters of width that are left.
          */}
          {group.eyebrow === null ? null : (
            <h2
              className={collapsed ? 'tf-nav__eyebrow tf-visually-hidden' : 'tf-nav__eyebrow'}
              id={`tf-nav-${group.key}`}
            >
              {group.eyebrow}
            </h2>
          )}
          {group.items.map((item) => renderLink(item, 'tf-nav__link', content(item)))}
        </div>
      ))}
    </nav>
  );
}
